import { MarkerType } from '@xyflow/react'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { JourneyEdge } from './types/journey-nodes'

export const EDGE_ID_PREFIX = 'eid-'
export const CONVERGENCE_PLACEHOLDER_SIZE = 12

/** Prefix for a VARIANT_SPLIT per-branch port id / source handle: `branch-${branch.key}`. */
export const BRANCH_HANDLE_PREFIX = 'branch-'

/**
 * Synthetic per-variant performance card node.
 *
 * Each variant lane of a VARIANT_SPLIT gets a real ELK node at the head of the
 * lane (between the split's per-variant port and the variant path's first real
 * step). ELK lays these out — it centers them in the lane, equalizes lane
 * widths, and routes the real edges through them — so the variant label is
 * centered under the lane's 90° turn and the (+) buttons become ordinary
 * on-edge add buttons.
 *
 * CRITICAL: these nodes live ONLY in the rendered graph projection. They are
 * injected in use-rendered-journey-graph just before ELK runs and never enter
 * canonical state, serialization, validation, dirty-detection, or enrichment.
 * They mirror the convergence-placeholder SIZING mechanism (fixed pre-measure
 * dimensions in node-dimensions.ts) but, unlike convergence dots, are NOT
 * canonical nodes.
 */
export const PERF_CARD_NODE_TYPE = 'perf-card'
export const PERF_CARD_NODE_ID_PREFIX = 'perf-card-'
export const PERF_CARD_EDGE_ID_PREFIX = 'perf-card-edge-'
// Pre-measurement dimensions used by ELK before React Flow measures the real
// DOM node. Must match the card CSS so the first ELK pass is geometrically
// accurate (see Known Fragility: Convergence Placeholder Sizing). The card is a
// fixed size so every lane equalizes — this is what aligns the lower (+)
// buttons across variants.
export const PERF_CARD_WIDTH = 250
// Exact fixed height of the fork-chip content: the two-line header (VARIANT · %
// eyebrow + name title, 43px) + the single metrics row (39px) + the card's 1px
// top/bottom borders. Sized to the content so the card ends flush at the bottom
// of the metrics band — no trailing white space below it. The wrapper renders at
// exactly this height (perf-card-node uses `height`, not `minHeight`), so the
// source handle sits flush at the visible card bottom and the card→step edge
// connects cleanly. A single fixed size also equalizes every variant lane, which
// keeps the lower (+) buttons aligned across variants. (ELK re-flows on any change
// to this — the perf card is a real ELK node — so edges/routing stay correct
// automatically.) Kept in sync with the AQE VariantPerformanceCard's --space-*
// based padding; re-measure in Storybook if that card's spacing changes.
export const PERF_CARD_HEIGHT = 84

export const getEdgeId = (id: number | string) => `${EDGE_ID_PREFIX}${id}`
export const DEFAULT_CUSTOM_EDGE_CONFIG: Partial<JourneyEdge> = {
    type: 'custom',
    markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 8,
        color: 'var(--color-gray-dark)',
    },
}

export const DEFAULT_BRANCH_EDGE_CONFIG: Partial<JourneyEdge> = {
    type: 'branch-edge',
    markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 8,
        color: 'var(--color-gray-dark)',
    },
}

export const RECOMMENDATION_NOTIFICATION_INTEGRATIONS = [DomainIntegrationProviders.BLUECONIC_REC]
